// Copyright 2025 Imprivata, Inc. All rights reserved.

import { Button, ImprSwitch, InputBox, Modal } from '@imprivata-cloud/components';
import { ErrorCode } from '@imprivata-cloud/authn';
import { Form } from 'antd';
import moment from 'moment';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getTemporaryCodeMetadata, imprIdpUsersSave } from '../../../../api/portalServices';
import {
  Action,
  addAdminRoleTemplate,
  buildAddUserRole,
  findRole,
  type ImprPhoneNumber,
  type ImprUser,
  type ImprUserEmail,
  type ImprUserRole,
  ImprUserRoleValue,
  isAdmin,
} from '../../../../api/types';
import { createCodingCtxV2 } from '../../../../api/utils';
import CopyButton from '../../../../components/copyButton/copyButton';
import { useErrorContext } from '../../../../errorHandler/context/ErrorContext';
import { type ApiError, AppError } from '../../../../errorHandler/errors';
import { formatDateInUtcWithTz, Regex } from '../../../../utils/utils';
import { useAssignTempCode } from '.././hooks/useTempCode';
import { ErrorCode as ErrorNotification } from '@/errorHandler/constants';

import './UserModal.less';

enum FormFieldNames {
  Username = 'Username',
  FirstName = 'FirstName',
  LastName = 'LastName',
  Email = 'Email',
  Phone = 'Phone',
  IsAdmin = 'IsAdmin',
  TemporaryAccessCode = 'TemporaryAccessCode',
}

interface FormValues {
  Username: string;
  FirstName: string;
  LastName: string;
  Email: string;
  Phone: string;
  IsAdmin: boolean;
  TemporaryAccessCode: string;
}

const TRANSLATION_ROOT = 'portal.settings.identity-users.user-modal';

function getDomain(user: ImprUser | undefined): string | undefined {
  return user?.username?.split('@')[1];
}

function isJitUser(editUserData: ImprUser | undefined, domain: string | undefined): boolean {
  const userDomain = getDomain(editUserData);
  return !!userDomain && !!domain && userDomain.toLowerCase() !== domain.toLowerCase();
}

function buildPayload(values: FormValues, domain: string, editUserData?: ImprUser) {
  const isUserUpdate = !!editUserData?.userId;
  const jitUser = isJitUser(editUserData, domain);
  let updatedPhoneNumbers: ImprPhoneNumber[] = [];
  let updatedEmails: ImprUserEmail[] = [];
  let updatedRoles: ImprUserRole[] = [];
  if (isUserUpdate) {
    //email updated
    if (
      editUserData?.emails &&
      editUserData?.emails?.length > 0 &&
      values.Email?.trim() !== editUserData?.emails[0]?.value
    ) {
      updatedEmails = [
        {
          emailId: editUserData?.emails[0]?.emailId,
          value: editUserData?.emails[0]?.value ?? '',
          isPrimary: true,
          action: Action.REMOVE,
        },
        { value: values.Email?.trim(), isPrimary: true, action: Action.ADD },
      ];
    } else if (editUserData?.emails?.length === 0) {
      //handle the case where user is created on setup and does not have an email.
      updatedEmails = [{ value: values.Email?.trim(), isPrimary: true, action: Action.ADD }];
    }
    //phone updated
    if (
      editUserData?.phoneNumbers &&
      editUserData?.phoneNumbers?.length > 0 &&
      values.Phone?.trim() !== editUserData?.phoneNumbers[0]?.value
    ) {
      updatedPhoneNumbers = [
        {
          value: editUserData?.phoneNumbers[0]?.value ?? '',
          isPrimary: true,
          phoneNumberId: editUserData?.phoneNumbers[0]?.phoneNumberId,
          action: Action.REMOVE,
        },
      ];
      if (values.Phone?.trim()) {
        updatedPhoneNumbers?.push({
          value: values.Phone?.trim(),
          isPrimary: true,
          action: Action.ADD,
        });
      }
    } else if (editUserData?.phoneNumbers?.length === 0) {
      updatedPhoneNumbers = values.Phone?.trim()
        ? [{ value: values.Phone?.trim(), isPrimary: true, action: Action.ADD }]
        : [];
    }
    if (values.IsAdmin !== isAdmin(editUserData)) {
      // Beware of the order, or you may get error "Role value already exists" or "Only one primary role is allowed"
      const currentUserRole = findRole(ImprUserRoleValue.user, editUserData.roles);
      if (currentUserRole) {
        currentUserRole.action = Action.REMOVE;
        updatedRoles.push(currentUserRole);
      }
      const currentAdminRole = findRole(ImprUserRoleValue.admin, editUserData.roles);
      if (currentAdminRole) {
        currentAdminRole.action = Action.REMOVE;
        updatedRoles.push(currentAdminRole, buildAddUserRole(true));
      } else {
        updatedRoles.push(addAdminRoleTemplate, buildAddUserRole(false));
      }
    }
  } else {
    updatedPhoneNumbers = values.Phone ? [{ value: values.Phone?.trim(), isPrimary: true }] : [];
    updatedEmails = [{ value: values.Email?.trim(), isPrimary: true }];
    if (values.IsAdmin) {
      updatedRoles = [addAdminRoleTemplate, buildAddUserRole(false)];
    } else {
      updatedRoles = [buildAddUserRole(true)];
    }
  }

  return {
    user: {
      userId: editUserData?.userId,
      username: jitUser ? values.Username?.trim() : `${values.Username?.trim()}@${domain}`,
      firstName: values.FirstName?.trim(),
      lastName: values.LastName?.trim(),
      emails: updatedEmails,
      phoneNumbers: updatedPhoneNumbers,
      roles: updatedRoles,
    },
  };
}

const UserModal = ({
  domain,
  closeModal,
  refetchUsers,
  editUserData,
  adminDeleteDisabled,
  isCurrentUser,
}: {
  domain?: string;
  closeModal: () => void;
  refetchUsers?: () => Promise<unknown>;
  // if empty means new user modal
  editUserData?: ImprUser;
  adminDeleteDisabled?: boolean;
  isCurrentUser?: boolean;
}) => {
  const { t } = useTranslation();
  const { emitError, clearErrorNotifications } = useErrorContext();
  const [form] = Form.useForm<FormValues>();
  const [isLoading, setIsLoading] = useState(false);
  const [tempAccessCode, setTempAccessCode] = useState('');
  const { mutate: assignTempCode, isPending } = useAssignTempCode();
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const jitUser = isJitUser(editUserData, domain);

  const getTemporaryCodeExpiration = useCallback(
    async (userId: string) => {
      try {
        const metadata = await getTemporaryCodeMetadata({ userId });
        const expiresAt = metadata.expiresAt;

        if (moment().isBefore(expiresAt)) {
          const formattedExpire = formatDateInUtcWithTz(expiresAt);
          setExpiresAt(formattedExpire);
        } else {
          setExpiresAt(null);
        }
      } catch (error) {
        setExpiresAt(null);
        if (((error as ApiError).code as string) === ErrorCode.NOT_FOUND) {
          console.trace('Temp code not found for user: ', userId, error);
          return;
        }
        console.error('Error fetching temp code metadata: ', userId, error);
        emitError(new AppError(error as ApiError));
      }
    },
    [emitError],
  );

  useEffect(() => {
    if (!editUserData?.userId) {
      return;
    }
    void getTemporaryCodeExpiration(editUserData.userId);
  }, [editUserData?.userId, getTemporaryCodeExpiration]);

  const hasUsernameChanged = (values: FormValues) => {
    if (!editUserData?.username) {
      return false;
    }
    const originalUsername = jitUser ? editUserData.username : editUserData.username.split('@')[0];
    return values.Username?.trim() !== originalUsername;
  };

  const generateAccessCode = async () => {
    const userId = editUserData?.userId;
    if (!userId) {
      console.log('UserId was not found.');
      return;
    }
    const values = await form.validateFields();
    if (hasUsernameChanged(values)) {
      const payload = buildPayload(values, domain || '', editUserData);
      await imprIdpUsersSave(payload);
    }

    try {
      const codingCtx = await createCodingCtxV2();

      assignTempCode(
        { request: { userId }, codingContext: codingCtx },
        {
          onSuccess: async response => {
            try {
              const decryptedTempCode = codingCtx.decryptToString(response.tempCode);
              setTempAccessCode(decryptedTempCode);
              form.setFieldValue(FormFieldNames.TemporaryAccessCode, decryptedTempCode);
              await getTemporaryCodeExpiration(userId);
            } catch (decryptError) {
              console.error('Error decrypting temp code:', decryptError);
              emitError(new AppError(decryptError as ApiError));
            }
          },
          onError: (e: unknown) => {
            console.error('Error generating temporary code:', e);
            emitError(new AppError(e as ApiError));
          },
        },
      );
    } catch (err) {
      console.error('Error generating or decrypting access code:', err);
      emitError(new AppError(err as ApiError));
    }
  };

  useEffect(() => {
    if (editUserData) {
      form.setFieldsValue({
        Username: jitUser ? editUserData.username : editUserData.username?.split('@')[0],
        FirstName: editUserData.firstName,
        LastName: editUserData.lastName,
        Email: editUserData.emails?.find(email => email.isPrimary)?.value,
        Phone: editUserData.phoneNumbers?.find(phone => phone.isPrimary)?.value,
        IsAdmin: isAdmin(editUserData),
      });
    } else {
      form.resetFields();
    }
  }, [editUserData, form, jitUser]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const usernameRegex = jitUser ? /^[a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+$/ : /^[a-zA-Z0-9._-]+$/;
      if (!usernameRegex.test(values.Username)) {
        console.log('Username has special characters. Validation failed.');
        emitError(new AppError({ code: ErrorNotification.INVALID_ARGUMENT }));
        return;
      }
      setIsLoading(true);
      clearErrorNotifications();
      const payload = buildPayload(values, domain || '', editUserData);
      await imprIdpUsersSave(payload);
      await new Promise(resolve => setTimeout(resolve, 1_000));
      await refetchUsers?.();
      closeModal();
    } catch (e) {
      setIsLoading(false);
      console.log('Error saving user:', e);
      // biome-ignore lint/suspicious/noExplicitAny: Needed
      const error = e as any;
      if (error.code === ErrorNotification.ALREADY_EXISTS) {
        console.log('Username already exists');
        emitError(new AppError({ code: ErrorNotification.ALREADY_EXISTS }));
      } else {
        console.log('Error saving user:', error);
        emitError(new AppError(error));
      }
    }
  };

  return (
    <Modal
      aria-label="user-modal"
      title={
        editUserData
          ? t(`${TRANSLATION_ROOT}.title-edit`, { username: editUserData.username })
          : t(`${TRANSLATION_ROOT}.title-add`)
      }
      className="user-modal"
      open={true}
      width={750}
      onCancel={closeModal}
      footer={null}
      maskClosable={false}
    >
      <Form
        name="UserForm"
        form={form}
        layout="vertical"
        requiredMark={false}
        onFinish={handleSubmit}
      >
        <div className="row" style={{ alignItems: 'center' }}>
          <Form.Item name={FormFieldNames.Username} label={t(`${TRANSLATION_ROOT}.input.username`)}>
            <InputBox
              aria-label="username-input"
              autoFocus
              autofocusDelayMs={300}
              minLength={6}
              maxLength={jitUser ? 100 : 30}
              required
              size="large"
            />
          </Form.Item>
          {!jitUser && <span className="post-label">@{domain}</span>}
        </div>

        <div className="row">
          <Form.Item
            name={FormFieldNames.FirstName}
            label={t(`${TRANSLATION_ROOT}.input.first-name`)}
          >
            <InputBox size="large" aria-label="first-name-input" />
          </Form.Item>
          <Form.Item
            name={FormFieldNames.LastName}
            label={t(`${TRANSLATION_ROOT}.input.last-name`)}
          >
            <InputBox size="large" aria-label="last-name-input" />
          </Form.Item>
        </div>

        <Form.Item name={FormFieldNames.Email} label={t(`${TRANSLATION_ROOT}.input.email`)}>
          <InputBox aria-label="email-input" maxLength={254} required size="large" type="email" />
        </Form.Item>

        <div className="row">
          <Form.Item
            name={FormFieldNames.Phone}
            label={t(`${TRANSLATION_ROOT}.input.phone`)}
            rules={[
              { pattern: Regex.phone, message: t(`${TRANSLATION_ROOT}.error.phone-validation`) },
            ]}
          >
            <InputBox minLength={9} maxLength={20} size="large" aria-label="phone-input" />
          </Form.Item>
          <div />
          {/* this div is for the visual formatting */}
        </div>

        {/* Temporary access code */}
        {!!editUserData && (
          <div className="row">
            <Form.Item
              name={FormFieldNames.TemporaryAccessCode}
              label={t(`${TRANSLATION_ROOT}.input.temp-code`)}
            >
              <div className="temp-access-row">
                <InputBox
                  aria-label="temp-access-code-input"
                  size="large"
                  readOnly
                  value={tempAccessCode}
                />
                <CopyButton
                  textToCopy={form.getFieldValue(FormFieldNames.TemporaryAccessCode)}
                  disabled={!form.getFieldValue(FormFieldNames.TemporaryAccessCode)}
                  className="copy-temp-code"
                  dynamicLabel={{
                    initialLabel: t('common.copy'),
                    labelAfterClick: t('common.copied'),
                    dynamicLabelDuration: 2000,
                  }}
                />
                <Button
                  label={t('common.generate-new')}
                  type="text"
                  onClick={generateAccessCode}
                  loading={isPending}
                />
              </div>
              <div className="temp-access-description">
                {expiresAt
                  ? t(`${TRANSLATION_ROOT}.input.temp-code-description`, { expiresAt })
                  : ''}
              </div>
            </Form.Item>
          </div>
        )}

        {/* Switch: User is an administrator */}
        <div className="row">
          <Form.Item name={FormFieldNames.IsAdmin} valuePropName="checked">
            <ImprSwitch
              switchLabel={{
                labelOn: t(`${TRANSLATION_ROOT}.input.isAdmin`),
                labelOff: t(`${TRANSLATION_ROOT}.input.isAdmin`),
              }}
              disabled={isAdmin(editUserData) && (adminDeleteDisabled || isCurrentUser)}
            />
          </Form.Item>
        </div>

        {/* Footer: Cancel and Save buttons */}
        <div className="form-footer-right">
          <Button aria-label="button-close" onClick={closeModal} label={t('common.close')} />
          <Form.Item shouldUpdate>
            {form => {
              const values = form.getFieldsValue();
              const isSubmitEnabled = !!(values.Username?.trim() && values.Email?.trim());
              return (
                <Button
                  type="primary"
                  htmlType="submit"
                  label={t('common.save')}
                  disabled={isLoading || !isSubmitEnabled}
                />
              );
            }}
          </Form.Item>
        </div>
      </Form>
    </Modal>
  );
};

export default UserModal;
